import React, { useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useSearchParams } from 'react-router-dom';
import kkpService from '../../services/kkpService';
import KkpColorBadge from '../../components/kkp/KkpColorBadge';
import KkpPlanningStatutDisplay from '../../components/kkp/KkpPlanningStatutDisplay';
import KkpVenteMagModal from '../../components/kkp/KkpVenteMagModal';
import { suggestBriefWizardStep, briefStatusWizardStep } from '../../utils/kkpVenteBriefUtils';
import KkpPlanningMagInlineCell from '../../components/kkp/KkpPlanningMagInlineCell';
import {
  filterStatutsForMagContext,
  filterStatutsForMagMonth,
  filterRefsForMagContext,
  filterRefsForMagMonth,
  canonicalMagPeriod,
} from '../../utils/kkpMagPeriodUtils';
import KkpPlanningStickyTableWrap from '../../components/kkp/KkpPlanningStickyTableWrap';
import { AppFilterSelect, AppFilterCommercialSelect, AppFilterField } from '../../components/filters';
import { AppAlert, AppButton } from '../../components/ui';
import KkpPlanningImportModal from '../../components/kkp/KkpPlanningImportModal';
import KkpPublicationLinkButton from '../../components/kkp/KkpPublicationLinkButton';
import KkpPubImageButton from '../../components/kkp/KkpPubImageButton';
import KkpBatValideModal from '../../components/kkp/KkpBatValideModal';
import SortableTh from '../../components/common/SortableTh';
import KkpClientAutocomplete from '../../components/kkp/KkpClientAutocomplete';
import { mergePlanningRow } from '../../utils/kkpPlanningMergeUtils';
import { confirmVenteDelete, confirmVenteClear } from '../../utils/confirmVenteDelete';
import { showViewportNotice } from '../../utils/showViewportNotice';
import { notify } from '../../components/common/AppToast';
import { formatPlanningImportMessage } from '../../utils/kkpPlanningImport';
import {
  buildClientInlinePayload,
  clientInlineDraftEquals,
  getClientDraftLabel,
  getClientInlineDraft,
} from '../../utils/kkpPlanningClientInline';
import { filterRowsByMulti, joinOrEmpty, singleOrEmpty, firstOrFallback, buildYearFilterOptions } from '../../utils/kkpPlanningFilters';
import {
  normalizeMultiFilter,
  normalizeTextFilter,
} from '../../utils/kkpPlanningFiltersStorage';
import {
  getPlanningClientDisplay,
  getPlanningViewEmptyMessage,
  isPlanningIndispoWithoutClient,
  PLANNING_VIEW_CLIENTS,
} from '../../utils/kkpPlanningViewMode';
import { useKkpPublications } from '../../hooks/useKkpPublications';
import { resolveCommercialSelectionFromDolibarrClient } from '../../utils/kkpDolibarrCommercialPrefill';
import usePlanningVentesPage from '../../hooks/usePlanningVentesPage';
import KkpPlanningPageShell, { KkpPlanningEmptyRow } from '../../components/kkp/KkpPlanningPageShell';
import { importPeriodLabelFromYearMonth } from '../../utils/kkpPlanningFormat';

const buildMagFilters = (defaultPeriod, commercialId = '') => {
  const now = new Date();
  const period = canonicalMagPeriod(
    defaultPeriod?.annee ?? now.getFullYear(),
    Number(defaultPeriod?.mois ?? now.getMonth() + 1),
  );

  return {
    annee: [String(period.annee)],
    mois: [String(period.mois)],
    commercial_id: commercialId ? [String(commercialId)] : [],
    format: [],
    contenu: [],
    statut_phase: [],
    search: '',
  };
};

const MAG_FILTERS_STORAGE_KEY = 'kkp_planning_mag_filters';

const normalizeMagFilters = (savedFilters, defaultPeriod) => {
  const defaults = buildMagFilters(defaultPeriod, '');
  const source = savedFilters && typeof savedFilters === 'object' ? savedFilters : {};

  return {
    // Période métier (mois en cours, ou suivant si CDF validé / butoir dépassée).
    annee: defaults.annee,
    mois: defaults.mois,
    commercial_id: normalizeMultiFilter(source.commercial_id),
    format: normalizeMultiFilter(source.format),
    contenu: normalizeMultiFilter(source.contenu),
    statut_phase: normalizeMultiFilter(source.statut_phase),
    search: normalizeTextFilter(source.search, ''),
  };
};

const toMagApiFilters = (filters) => ({
  annee: joinOrEmpty(filters.annee),
  mois: joinOrEmpty(filters.mois),
  commercial_id: joinOrEmpty(filters.commercial_id),
  format: joinOrEmpty(filters.format),
  contenu: joinOrEmpty(filters.contenu),
  statut_phase: joinOrEmpty(filters.statut_phase),
  search: filters.search ?? '',
});

const TABLE_SORT_COLUMNS = {
  periode: { type: 'number', getValue: (row) => (row.annee ?? 0) * 100 + (row.mois ?? 0) },
  commercial: { type: 'string', getValue: (row) => row.commercial_nom },
  client: { type: 'string', getValue: (row) => getPlanningClientDisplay(row, '') },
  format: { type: 'string', getValue: (row) => row.format },
  contenu: { type: 'string', getValue: (row) => row.contenu },
  statut: { type: 'string', getValue: (row) => row.statut_phase },
  cdf: { type: 'string', getValue: (row) => row.cdf_emplacement ?? '' },
  commentaire: { type: 'string', getValue: (row) => row.commentaire },
};

const KkpPlanningMagPage = () => {
  const [searchParams] = useSearchParams();
  const { getPublicationUrl } = useKkpPublications();

  const fetchMeta = useCallback(async () => {
    try {
      return await kkpService.getMagMeta();
    } catch (err) {
      notify.error(err.message);
      throw err;
    }
  }, []);

  const initFiltersFromMeta = useCallback((meta, savedFilters) => {
    const filtersFromMeta = normalizeMagFilters(savedFilters, meta.default_period);
    const anneeParam = Number(searchParams.get('annee'));
    const moisParam = Number(searchParams.get('mois'));
    const hasAnneeParam = !Number.isNaN(anneeParam) && anneeParam > 0;
    const hasMoisParam = !Number.isNaN(moisParam) && moisParam > 0;
    if (hasMoisParam) {
      const yearForPeriod = hasAnneeParam ? anneeParam : Number(filtersFromMeta.annee?.[0]);
      const period = canonicalMagPeriod(yearForPeriod, moisParam);
      filtersFromMeta.annee = [String(period.annee)];
      filtersFromMeta.mois = [String(period.mois)];
    } else if (hasAnneeParam) {
      filtersFromMeta.annee = [String(anneeParam)];
    }
    return filtersFromMeta;
  }, [searchParams]);

  const fetchVentes = useCallback(async (filters, { page, viewMode }) => {
    try {
      const apiFilters = toMagApiFilters(filters);
      const result = await kkpService.listVentesMag({
        ...apiFilters,
        view_mode: viewMode,
        page,
        limit: 50,
      });
      let nextVentes = result.ventes;
      nextVentes = filterRowsByMulti(nextVentes, filters.annee, (v) => canonicalMagPeriod(v.annee, v.mois).annee);
      nextVentes = filterRowsByMulti(nextVentes, filters.mois, (v) => canonicalMagPeriod(v.annee, v.mois).mois);
      nextVentes = filterRowsByMulti(nextVentes, filters.commercial_id, (v) => v.commercial_id);
      nextVentes = filterRowsByMulti(nextVentes, filters.format, (v) => v.format);
      nextVentes = filterRowsByMulti(nextVentes, filters.contenu, (v) => v.contenu);
      nextVentes = filterRowsByMulti(nextVentes, filters.statut_phase, (v) => v.statut_phase);
      return {
        ventes: nextVentes,
        pagination: result.pagination,
        viewModeCounts: result.viewModeCounts ?? { clients: 0, dispos: 0 },
        extras: { cdfValidated: result.cdfValidated },
      };
    } catch (err) {
      notify.error(err.message);
      throw err;
    }
  }, []);

  const {
    ask,
    confirmModal,
    meta,
    ventes,
    setVentes,
    commerciaux,
    defaultCommercialId,
    pagination,
    filters,
    setFilters,
    loading,
    error,
    setError,
    showModal,
    editingVente,
    modalInitialValues,
    modalWizardStep,
    openCreate,
    openEdit,
    closeModal,
    setImporting,
    setExporting,
    showImportModal,
    setShowImportModal,
    importStrategy,
    setImportStrategy,
    wipeScope,
    setWipeScope,
    fileInputRef,
    editingCell,
    setEditingCell,
    editDraft,
    setEditDraft,
    inlineSaving,
    setInlineSaving,
    batValideVente,
    batValideSubmitting,
    batValideError,
    openBatValide,
    closeBatValide,
    submitBatValide,
    statutSelectProps,
    viewMode,
    setViewMode,
    changeViewMode,
    viewModeCounts,
    visibleVentes,
    sortedRows,
    sort,
    toggleSort,
    getSortIcon,
    commercialOptions,
    loadVentes,
    listExtras,
    buildImportExportMenu,
  } = usePlanningVentesPage({
    storageKey: MAG_FILTERS_STORAGE_KEY,
    tableSortColumns: TABLE_SORT_COLUMNS,
    fetchMeta,
    initFiltersFromMeta,
    fetchVentes,
    viewModeCountsSource: 'api',
  });

  const cdfValidated = Boolean(listExtras?.cdfValidated);
  const canDeleteVente = (vente) => (
    vente?.can_delete ?? !vente?.is_mag_structural
  );
  const canClearVente = (vente) => (
    vente?.can_clear ?? Boolean(vente?.id)
  );

  const anneeFilterSingle = singleOrEmpty(filters?.annee);
  const moisFilterSingle = singleOrEmpty(filters?.mois);
  const filterStatuts = useMemo(
    () => filterStatutsForMagContext(
      meta?.statuts,
      anneeFilterSingle ? Number(anneeFilterSingle) : null,
      moisFilterSingle ? Number(moisFilterSingle) : null,
    ),
    [meta?.statuts, anneeFilterSingle, moisFilterSingle],
  );

  const filterContenus = useMemo(
    () => filterRefsForMagContext(
      meta?.contenus,
      anneeFilterSingle ? Number(anneeFilterSingle) : null,
      moisFilterSingle ? Number(moisFilterSingle) : null,
    ),
    [meta?.contenus, anneeFilterSingle, moisFilterSingle],
  );

  const anneeOptions = useMemo(
    () => buildYearFilterOptions(filters?.annee),
    [filters?.annee],
  );
  const moisOptions = useMemo(
    () => (meta?.mois ?? []).map((m) => ({ value: String(m.value), label: m.label })),
    [meta?.mois],
  );
  const contenuOptions = useMemo(
    () => filterContenus.map((c) => ({ value: c.value, label: c.value })),
    [filterContenus],
  );
  const formatOptions = useMemo(
    () => filterRefsForMagContext(
      meta?.formats,
      anneeFilterSingle ? Number(anneeFilterSingle) : null,
      moisFilterSingle ? Number(moisFilterSingle) : null,
    ).map((f) => ({ value: f.value, label: f.value })),
    [meta?.formats, anneeFilterSingle, moisFilterSingle],
  );
  const statutOptions = useMemo(
    () => filterStatuts.map((s) => ({ value: s.value, label: s.value })),
    [filterStatuts],
  );

  const handleOpenCreate = () => {
    openCreate({
      annee: Number(firstOrFallback(filters?.annee, new Date().getFullYear())),
      mois: Number(firstOrFallback(filters?.mois, new Date().getMonth() + 1)),
    });
  };

  const handleCreaPubChange = (venteId, pieces) => {
    setVentes((prev) => prev.map((row) => (
      row.id === venteId ? { ...row, crea_pub_pieces_jointes: pieces } : row
    )));
  };

  const handleOpenEdit = (vente, wizardStep = undefined) => {
    openEdit(vente, typeof wizardStep === 'number' ? wizardStep : suggestBriefWizardStep(vente));
  };

  const resetFilters = () => {
    setViewMode(PLANNING_VIEW_CLIENTS);
    setFilters(buildMagFilters(meta?.default_period, ''));
  };

  const handleDelete = async (venteOrId) => {
    const vente = typeof venteOrId === 'object'
      ? venteOrId
      : ventes.find((v) => v.id === venteOrId);
    const id = vente?.id ?? venteOrId;
    if (!id) return;
    if (!(await confirmVenteDelete(ask, vente?.client_label))) return;
    try {
      await kkpService.deleteVenteMag(id);
      loadVentes(pagination.page || 1);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const handleClear = async (venteOrId) => {
    const vente = typeof venteOrId === 'object'
      ? venteOrId
      : ventes.find((v) => v.id === venteOrId);
    const id = vente?.id ?? venteOrId;
    if (!id) return;
    if (!(await confirmVenteClear(ask, vente?.client_label))) return;
    try {
      await kkpService.clearVenteMag(id);
      loadVentes(pagination.page || 1);
    } catch (err) {
      notify.error(err.message);
    }
  };

  const getInlineDraft = (vente, field) => {
    switch (field) {
      case 'periode':
        return { annee: vente.annee, mois: vente.mois };
      case 'commercial_id':
        return vente.commercial_id != null ? String(vente.commercial_id) : '';
      case 'client_label':
        return getClientInlineDraft(vente);
      case 'format':
        return vente.format ?? '';
      case 'contenu':
        return vente.contenu ?? '';
      case 'statut_phase':
        return vente.statut_phase ?? '';
      case 'commentaire':
        return vente.commentaire ?? '';
      default:
        return '';
    }
  };

  const startInlineEdit = async (venteId, field) => {
    if (!venteId || !field) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }
    if (editingCell && (editingCell.venteId !== venteId || editingCell.field !== field)) {
      await saveInlineEdit();
    }
    const vente = ventes.find((v) => v.id === venteId);
    if (!vente) return;
    setEditingCell({ venteId, field });
    setEditDraft(getInlineDraft(vente, field));
  };

  const buildInlinePayload = (field, draft) => {
    switch (field) {
      case 'periode':
        return { annee: draft.annee, mois: draft.mois };
      case 'commercial_id':
        return { commercial_id: draft || null };
      case 'client_label':
        return buildClientInlinePayload(draft);
      case 'format':
        return { format: draft };
      case 'contenu':
        return { contenu: draft };
      case 'statut_phase':
        return { statut_phase: draft ?? '' };
      case 'commentaire':
        return { commentaire: draft || null };
      default:
        return {};
    }
  };

  const saveInlineEdit = async (draftOverride) => {
    if (!editingCell || inlineSaving) return;
    const vente = ventes.find((v) => v.id === editingCell.venteId);
    if (!vente) return;

    const draft = draftOverride !== undefined ? draftOverride : editDraft;
    const original = getInlineDraft(vente, editingCell.field);
    const unchanged = editingCell.field === 'client_label'
      ? clientInlineDraftEquals(draft, vente)
      : JSON.stringify(draft) === JSON.stringify(original);
    if (unchanged) {
      setEditingCell(null);
      setEditDraft(null);
      return;
    }

    if (editingCell.field === 'client_label' && !String(getClientDraftLabel(draft)).trim()) {
      notify.error('Le client ne peut pas être vide.');
      return;
    }

    setInlineSaving(true);
    setError(null);
    try {
      const payload = buildInlinePayload(editingCell.field, draft);
      const { vente: updated, notice } = await kkpService.updateVenteMag(vente.id, payload);
      if (notice && editingCell.field === 'client_label') {
        showViewportNotice(notice);
      }
      setVentes((prev) => mergePlanningRow(prev, updated));
      setEditingCell(null);
      setEditDraft(null);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setInlineSaving(false);
    }
  };

  const handleExport = async () => {
    if (!filters) return;
    setExporting(true);
    setError(null);
    try {
      await kkpService.exportVentesMag({
        annee: singleOrEmpty(filters.annee),
        mois: singleOrEmpty(filters.mois),
      });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  const importPeriodLabel = useMemo(
    () => importPeriodLabelFromYearMonth(
      singleOrEmpty(filters?.annee),
      singleOrEmpty(filters?.mois),
      moisOptions,
    ),
    [filters?.annee, filters?.mois, moisOptions],
  );

  const handleImportClick = () => {
    setImportStrategy('upsert');
    setWipeScope(singleOrEmpty(filters?.annee) && singleOrEmpty(filters?.mois) ? 'month' : 'all');
    setShowImportModal(true);
  };

  const handleImportConfirm = () => {
    if (importStrategy === 'wipe' && wipeScope === 'month' && (!singleOrEmpty(filters.annee) || !singleOrEmpty(filters.mois))) {
      notify.error('Sélectionnez une seule année et un seul mois dans les filtres pour remplacer une période.');
      return;
    }
    setShowImportModal(false);
    fileInputRef.current?.click();
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImporting(true);
    setError(null);
    try {
      const result = await kkpService.importVentesMag(file, {
        upsert: importStrategy === 'upsert',
        replace: importStrategy === 'wipe' && wipeScope === 'month',
        replaceAll: importStrategy === 'wipe' && wipeScope === 'all',
        annee: importStrategy === 'wipe' && wipeScope === 'month' ? singleOrEmpty(filters.annee) : '',
        mois: importStrategy === 'wipe' && wipeScope === 'month' ? singleOrEmpty(filters.mois) : '',
      });
      const msg = formatPlanningImportMessage(result);
      if ((result.errors?.length ?? 0) > 0) {
        notify.warning(`${msg} ${result.errors.length} erreur(s) — voir la console.`);
        console.warn('Import MAG erreurs', result.errors);
      } else {
        notify.success(msg);
      }
      loadVentes(1);
    } catch (err) {
      setError(err.message);
      notify.error(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <KkpPlanningPageShell
      title="Planning ventes MAG"
      subtitle="Suivi des ventes magazine par mois et commercial"
      menuItems={buildImportExportMenu(handleImportClick, handleExport)}
      error={error}
      banners={cdfValidated ? (
        <AppAlert variant="success" className="d-flex align-items-center gap-2 py-2 mb-3 kkp-mag-cdf-validated-banner">
          <Icon icon="mdi:check-decagram" className="text-xl flex-shrink-0" />
          <span className="text-sm mb-0">
            Le chemin de fer de cette période est <strong>validé</strong>.
          </span>
        </AppAlert>
      ) : null}
      fileInputRef={fileInputRef}
      onImportFile={handleImportFile}
      resetDisabled={!filters}
      onResetFilters={resetFilters}
      filterChildren={(
        <>
          <AppFilterSelect
            id="mag-annee"
            label="Année"
            col="col-6 col-md-2"
            value={filters?.annee ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, annee: next }))}
            options={anneeOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="mag-mois"
            label="Mois"
            col="col-6 col-md-2"
            value={filters?.mois ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, mois: next }))}
            options={moisOptions}
            disabled={!filters}
          />
          <AppFilterCommercialSelect
            id="mag-commercial"
            col="col-6 col-md-2"
            value={filters?.commercial_id ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, commercial_id: next }))}
            options={commercialOptions}
            disabled={!filters}
            multiple
          />
          <AppFilterSelect
            id="mag-format"
            label="Format"
            col="col-6 col-md-2"
            value={filters?.format ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, format: next }))}
            options={formatOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="mag-contenu"
            label="Contenu"
            col="col-6 col-md-2"
            value={filters?.contenu ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, contenu: next }))}
            options={contenuOptions}
            disabled={!filters}
          />
          <AppFilterSelect
            id="mag-statut"
            label="Statut"
            col="col-6 col-md-2"
            value={filters?.statut_phase ?? []}
            onChange={(next) => setFilters((f) => ({ ...f, statut_phase: next }))}
            options={statutOptions}
            disabled={!filters}
          />
          <AppFilterField col="col-12 col-md-4" label="Client">
            <KkpClientAutocomplete
              value={filters?.search ?? ''}
              onChange={(val) => setFilters((f) => ({ ...f, search: val }))}
              onSelect={async (client) => {
                const label = String(client?.nom ?? client?.label ?? '').trim();
                try {
                  const matchedCommercial = await resolveCommercialSelectionFromDolibarrClient(client, commercialOptions);
                  setFilters((f) => ({
                    ...f,
                    search: label,
                    commercial_id: matchedCommercial ? [matchedCommercial.value] : [],
                  }));
                } catch {
                  setFilters((f) => ({ ...f, search: label }));
                }
              }}
              disabled={!filters}
              size="sm"
              placeholder="Client ou tiers Dolibarr…"
            />
          </AppFilterField>
        </>
      )}
      viewMode={viewMode}
      onViewModeChange={changeViewMode}
      viewModeCounts={viewModeCounts}
      onCreate={handleOpenCreate}
      resultsCardClassName={cdfValidated ? 'kkp-mag-period-validated' : ''}
      loading={loading}
      table={(
        <KkpPlanningStickyTableWrap>
          <table className="table table-hover mb-0 kkp-planning-table kkp-planning-table--mag">
            <thead>
              <tr>
                <SortableTh sortKey="periode" label="Période" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commercial" label="Commercial" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="client" label="Client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="format" label="Format" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="contenu" label="Contenu" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="cdf" label="Emplacement CDF" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="statut" label="Statut" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh sortKey="commentaire" label="Commentaire" className="kkp-table-comment" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                <SortableTh label="Actions" className="text-end kkp-col-actions" sortable={false} />
              </tr>
            </thead>
            <tbody>
              {visibleVentes.length === 0 ? (
                <KkpPlanningEmptyRow
                  colSpan={9}
                  message={getPlanningViewEmptyMessage(viewMode)}
                  actions={[
                    {
                      key: 'reset',
                      label: 'Réinitialiser',
                      icon: 'mdi:filter-off-outline',
                      onClick: resetFilters,
                    },
                    {
                      key: 'create',
                      label: 'Nouvelle vente',
                      icon: 'mdi:plus',
                      variant: 'primary',
                      onClick: handleOpenCreate,
                    },
                  ]}
                />
              ) : sortedRows.map((v) => (
                <tr key={v.id} className={v.is_mag_structural ? 'kkp-mag-structural-row' : undefined}>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="periode"
                    type="periode"
                    options={meta?.mois ?? []}
                    optionValueKey="value"
                    optionLabelKey="label"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    <span className="text-sm">{v.mois_label} {v.annee}</span>
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="commercial_id"
                    type="select"
                    options={commerciaux}
                    optionValueKey="id"
                    optionLabelKey="nom"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    {v.commercial_nom ? (
                      <KkpColorBadge color={v.commercial_couleur} vivid>
                        {v.commercial_nom}
                      </KkpColorBadge>
                    ) : (
                      <span className="text-secondary-light">—</span>
                    )}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="client_label"
                    type="client"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="fw-medium"
                  >
                    <span className={isPlanningIndispoWithoutClient(v) ? 'text-secondary-light fst-italic' : undefined}>
                      {getPlanningClientDisplay(v)}
                    </span>
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="format"
                    type="select"
                    allowEmpty={false}
                    readOnly={!!v.is_mag_structural}
                    options={filterRefsForMagMonth(meta?.formats, v.annee, v.mois)}
                    optionValueKey="value"
                    optionLabelKey="value"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    <span className="text-sm">{v.format}</span>
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="contenu"
                    type="select"
                    allowEmpty={false}
                    options={filterRefsForMagMonth(meta?.contenus, v.annee, v.mois)}
                    optionValueKey="value"
                    optionLabelKey="value"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                  >
                    <span className="text-sm">{v.contenu}</span>
                  </KkpPlanningMagInlineCell>
                  <td className="text-sm kkp-cdf-emplacement-cell">
                    {v.cdf_page || v.cdf_position ? (
                      <>
                        <div className="fw-medium">{v.cdf_page}</div>
                        {v.cdf_position ? (
                          <div className="text-secondary-light">{v.cdf_position}</div>
                        ) : null}
                      </>
                    ) : (
                      <span className="text-secondary-light">—</span>
                    )}
                  </td>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="statut_phase"
                    type="select"
                    allowEmpty={!!v.is_mag_structural}
                    options={filterStatutsForMagMonth(meta?.statuts, v.annee, v.mois)}
                    optionValueKey="value"
                    optionLabelKey="value"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    {...statutSelectProps(v)}
                  >
                    {v.statut_phase ? (
                      <KkpPlanningStatutDisplay
                        vente={v}
                        onBriefClick={(briefStatus) => handleOpenEdit(v, briefStatusWizardStep(v, briefStatus))}
                      />
                    ) : (
                      <span className="text-secondary-light">—</span>
                    )}
                  </KkpPlanningMagInlineCell>
                  <KkpPlanningMagInlineCell
                    venteId={v.id}
                    field="commentaire"
                    type="text"
                    editingCell={editingCell}
                    editDraft={editDraft}
                    onEditDraftChange={setEditDraft}
                    onStartEdit={startInlineEdit}
                    onSave={saveInlineEdit}
                    saving={inlineSaving}
                    className="text-sm text-secondary-light kkp-table-comment"
                  >
                    {v.commentaire || '—'}
                  </KkpPlanningMagInlineCell>
                  <td className="text-end kkp-col-actions">
                    <div className="kkp-actions-toolbar">
                      <KkpPublicationLinkButton
                        url={getPublicationUrl('mag', v.annee, v.mois, v.publication_url)}
                        type="mag"
                      />
                      <KkpPubImageButton vente={v} onCreaPubChange={handleCreaPubChange} />
                      <AppButton
                        size="sm"
                        variant="outline-primary"
                        icon="mdi:pencil"
                        title="Modifier"
                        aria-label="Modifier"
                        onClick={() => handleOpenEdit(v)}
                      />
                      {canClearVente(v) && (
                        <AppButton
                          size="sm"
                          variant="outline-warning"
                          icon="mdi:eraser"
                          title="Effacer le contenu (conserve l'emplacement)"
                          aria-label="Effacer"
                          onClick={() => handleClear(v)}
                        />
                      )}
                      {canDeleteVente(v) && (
                        <AppButton
                          size="sm"
                          variant="outline-danger"
                          icon="mdi:delete"
                          title="Supprimer"
                          aria-label="Supprimer"
                          onClick={() => handleDelete(v)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </KkpPlanningStickyTableWrap>
      )}
      pagination={pagination}
      onPageChange={(page) => loadVentes(page)}
      modals={(
        <>
          <KkpVenteMagModal
            show={showModal}
            onClose={closeModal}
            onSaved={() => loadVentes(pagination.page || 1)}
            onDeleted={() => loadVentes(pagination.page || 1)}
            onCleared={() => loadVentes(pagination.page || 1)}
            onError={notify.error}
            meta={meta}
            commerciaux={commerciaux}
            defaultCommercialId={defaultCommercialId}
            vente={editingVente}
            initialValues={modalInitialValues}
            initialWizardStep={modalWizardStep}
            onRequestBatValide={(nextVente) => {
              closeModal();
              openBatValide(nextVente);
            }}
          />

          <KkpBatValideModal
            show={Boolean(batValideVente)}
            vente={batValideVente}
            onClose={closeBatValide}
            onSubmit={submitBatValide}
            submitting={batValideSubmitting}
            error={batValideError}
          />

          <KkpPlanningImportModal
            show={showImportModal}
            title="Importer le planning MAG"
            sheetHint="La feuille VENTE MAG du fichier sera lue automatiquement."
            canalLabel="MAG"
            strategy={importStrategy}
            onStrategyChange={setImportStrategy}
            wipeScope={wipeScope}
            onWipeScopeChange={setWipeScope}
            periodLabel={importPeriodLabel}
            onClose={() => setShowImportModal(false)}
            onConfirm={handleImportConfirm}
          />
          {confirmModal}
        </>
      )}
    />
  );
};

export default KkpPlanningMagPage;
